import React, { useEffect, useState } from "react";
import { emailSubscriptionsService, EmailSubscription } from "@/lib/supabase";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Mail,
  Search,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Loader2,
  Download,
} from "lucide-react";
import { formatDate } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { SidebarToggle, useAdminLayout } from "@/components/admin/AdminLayout";
import * as XLSX from "xlsx";

const AdminSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState<EmailSubscription[]>([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState<
    EmailSubscription[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [processingEmail, setProcessingEmail] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();
  const { isMobile } = useAdminLayout();

  const itemsPerPage = 10;

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredSubscriptions(subscriptions);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = subscriptions.filter(
        (sub) =>
          sub.email.toLowerCase().includes(query) ||
          (sub.name && sub.name.toLowerCase().includes(query))
      );
      setFilteredSubscriptions(filtered);
    }
    setCurrentPage(1);
  }, [searchQuery, subscriptions]);

  const fetchSubscriptions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await emailSubscriptionsService.getAll();
      setSubscriptions(data);
      setFilteredSubscriptions(data);
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
      setError("Không thể tải danh sách đăng ký. Vui lòng thử lại sau.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (
    email: string,
    newStatus: "active" | "unsubscribed"
  ) => {
    setProcessingEmail(email);
    try {
      let result;
      if (newStatus === "unsubscribed") {
        result = await emailSubscriptionsService.unsubscribe(email);
      } else {
        // Reactivate subscription (would need to add this method to the service)
        result = await emailSubscriptionsService.updatePreferences(email, {});
        // This is a workaround - we should add a reactivate method to the service
      }

      if (result.success) {
        // Update local state
        const updatedSubscriptions = subscriptions.map((sub) =>
          sub.email === email ? { ...sub, status: newStatus } : sub
        );
        setSubscriptions(updatedSubscriptions);

        toast({
          title: "Cập nhật thành công",
          description: `Email ${email} đã được ${
            newStatus === "active" ? "kích hoạt" : "hủy đăng ký"
          }.`,
          duration: 3000,
        });
      } else {
        toast({
          title: "Cập nhật không thành công",
          description: result.error || "Đã xảy ra lỗi khi cập nhật trạng thái.",
          variant: "destructive",
          duration: 3000,
        });
      }
    } catch (err) {
      console.error("Error updating subscription status:", err);
      toast({
        title: "Cập nhật không thành công",
        description: "Đã xảy ra lỗi khi cập nhật trạng thái.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setProcessingEmail(null);
    }
  };

  // Function to export subscriptions to Excel
  const exportToExcel = async () => {
    try {
      setIsExporting(true);

      // Nếu đang tìm kiếm, xuất danh sách được lọc, nếu không xuất tất cả
      const dataToExport =
        searchQuery.trim() !== "" ? filteredSubscriptions : subscriptions;

      // Tạo dữ liệu cho Excel
      const excelData = dataToExport.map((subscription) => ({
        Email: subscription.email,
        Tên: subscription.name || "",
        "Trạng thái":
          subscription.status === "active" ? "Đã kích hoạt" : "Đã hủy",
        "Ngày đăng ký": subscription.created_at
          ? formatDate(new Date(subscription.created_at))
          : "",
        Nguồn: subscription.source || "website",
      }));

      // Tạo workbook và worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Danh sách email");

      // Tùy chỉnh độ rộng cột
      const columnWidths = [
        { wch: 35 }, // Email
        { wch: 25 }, // Tên
        { wch: 15 }, // Trạng thái
        { wch: 15 }, // Ngày đăng ký
        { wch: 15 }, // Nguồn
      ];
      worksheet["!cols"] = columnWidths;

      // Tạo tên file với timestamp
      const fileName = `GlowHer_Email_Subscriptions_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      // Xuất file và tải xuống
      XLSX.writeFile(workbook, fileName);

      toast({
        title: "Xuất file thành công",
        description: `Đã xuất ${excelData.length} email ra file Excel.`,
        duration: 3000,
      });
    } catch (err) {
      console.error("Error exporting to Excel:", err);
      toast({
        title: "Xuất file không thành công",
        description: "Đã xảy ra lỗi khi xuất file. Vui lòng thử lại sau.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Pagination logic
  const pageCount = Math.ceil(filteredSubscriptions.length / itemsPerPage);
  const paginatedSubscriptions = filteredSubscriptions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          {isMobile && <SidebarToggle className="mr-2" />}
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Đăng ký nhận tin
            </h1>
            <p className="text-muted-foreground">
              Quản lý danh sách người đăng ký nhận tin
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={exportToExcel}
            disabled={isLoading || isExporting || subscriptions.length === 0}
            className="mr-2"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            <span className="hidden md:inline">Xuất Excel</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchSubscriptions}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-2 hidden md:inline">Làm mới</span>
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Tìm kiếm theo email hoặc tên..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Mail className="h-5 w-5 mr-2" />
            Danh sách đăng ký
          </CardTitle>
          <CardDescription>
            Tổng cộng {filteredSubscriptions.length} đăng ký
            {subscriptions.length !== filteredSubscriptions.length &&
              ` (lọc từ ${subscriptions.length})`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="flex items-center justify-center p-6 text-red-500">
              <AlertCircle className="h-5 w-5 mr-2" />
              <p>{error}</p>
            </div>
          ) : isLoading ? (
            <div className="flex justify-center items-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredSubscriptions.length === 0 ? (
            <div className="text-center p-6 text-muted-foreground">
              Không tìm thấy đăng ký nào.
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Tên</TableHead>
                      <TableHead>Trạng thái</TableHead>
                      <TableHead>Ngày đăng ký</TableHead>
                      <TableHead>Nguồn</TableHead>
                      <TableHead className="text-right">Thao tác</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedSubscriptions.map((subscription) => (
                      <TableRow key={subscription.id}>
                        <TableCell className="font-medium">
                          {subscription.email}
                        </TableCell>
                        <TableCell>{subscription.name || "-"}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              subscription.status === "active"
                                ? "success"
                                : "outline"
                            }
                          >
                            {subscription.status === "active"
                              ? "Đã kích hoạt"
                              : "Đã hủy"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {subscription.created_at
                            ? formatDate(new Date(subscription.created_at))
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {subscription.source || "website"}
                        </TableCell>
                        <TableCell className="text-right">
                          {processingEmail === subscription.email ? (
                            <Button size="sm" variant="ghost" disabled>
                              <Loader2 className="h-4 w-4 animate-spin" />
                            </Button>
                          ) : subscription.status === "active" ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                handleUpdateStatus(
                                  subscription.email,
                                  "unsubscribed"
                                )
                              }
                            >
                              Hủy đăng ký
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() =>
                                handleUpdateStatus(subscription.email, "active")
                              }
                            >
                              Kích hoạt lại
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {pageCount > 1 && (
                <div className="mt-4">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() =>
                            setCurrentPage(Math.max(1, currentPage - 1))
                          }
                          className={
                            currentPage === 1
                              ? "pointer-events-none opacity-50"
                              : "cursor-pointer"
                          }
                        />
                      </PaginationItem>

                      {Array.from(
                        { length: Math.min(5, pageCount) },
                        (_, i) => {
                          // Logic to show pages around current page
                          let pageNum;
                          if (pageCount <= 5) {
                            // Show all pages if 5 or fewer
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            // Show first 5 pages
                            pageNum = i + 1;
                          } else if (currentPage >= pageCount - 2) {
                            // Show last 5 pages
                            pageNum = pageCount - 4 + i;
                          } else {
                            // Show 2 before and 2 after current page
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <PaginationItem key={pageNum}>
                              <PaginationLink
                                onClick={() => setCurrentPage(pageNum)}
                                isActive={currentPage === pageNum}
                                className="cursor-pointer"
                              >
                                {pageNum}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        }
                      )}

                      <PaginationItem>
                        <PaginationNext
                          onClick={() =>
                            setCurrentPage(Math.min(pageCount, currentPage + 1))
                          }
                          className={
                            currentPage === pageCount
                              ? "pointer-events-none opacity-50"
                              : "cursor-pointer"
                          }
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSubscriptions;
